import { Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { InnerThoughtsEntity, TimelineWidgetProps } from '../types';

export interface InnerThoughtsWidgetProps extends TimelineWidgetProps<InnerThoughtsEntity> {
  debugMode?: boolean;
}

// Register renderer + visibility
registerWidgetRenderer(
  'inner_thoughts',
  ({ entity, debugMode }) => (
    <InnerThoughtsWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function InnerThoughtsWidget({ entity, debugMode = false }: InnerThoughtsWidgetProps) {
  const { status, text } = entity.props;
  const [expanded, setExpanded] = useState(true);

  const statusColor = status === 'completed' ? 'info' : status === 'error' ? 'error' : 'info';

  return (
    <Paper
      variant="outlined"
      className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 mp-block artifact-content"
    >
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {status === 'active'
            ? 'lightbulb'
            : status === 'completed'
              ? 'lightbulb'
              : 'error_outline'}
        </Icon>

        <div className="flex-1">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              💭 Inner Thoughts
            </Typography>
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'active' || status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {text ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {text}
                    </Typography>
                  </div>
                ) : null}

                {status === 'completed' && entity.props.error ? (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <Typography variant="body2" color="error">
                      Error: {entity.props.error}
                    </Typography>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="mt-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    {entity.props.error || 'An error occurred while processing inner thoughts'}
                  </Typography>
                </div>
                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2 mt-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}
